import {
  QBox,
  QFlex,
  QIconButton,
  QSpacer,
  QStack,
  QTooltip,
} from '@qualio/ui-components';
import { forwardRef, ReactNode, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EditorName } from '../../../config/handlers';
import { handleSidebarClick } from '../../../util/SidebarUtils';
import { BackToTop } from '../BackToTop';
import { EditorStatus, ZoomContext } from '../DocumentOverview/Context';
import { TableOfContents } from '../TableOfContents';
import styles from './DocumentLayout.module.css';

interface DocumentLayoutProps {
  isEditMode?: boolean;
  sections?: any[];
  sectionTitlesVisible?: boolean;
  commentsPermission?: number;
  editorStatus?: EditorStatus;
  editorName?: EditorName;
  children: ReactNode;
  contentId?: string;
  commentControlId?: string;
  sidebarId?: string;
  dataCy?: {
    content?: string;
    commentControl?: string;
    commentButton?: string;
    commentSidebar?: string;
  };
  isLandscape?: boolean;
  showComments?: boolean;
  showTOC?: boolean;
  isMultiRootEditor?: boolean;
}

export const DocumentLayout = forwardRef<HTMLDivElement, DocumentLayoutProps>(
  (
    {
      isEditMode = false,
      sections = [],
      sectionTitlesVisible = true,
      commentsPermission = 0,
      editorName,
      editorStatus,
      children,
      contentId,
      commentControlId,
      sidebarId,
      dataCy,
      isLandscape = true,
      showComments = true,
      showTOC = true,
      isMultiRootEditor = false,
    }: DocumentLayoutProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const commentsEnabled = showComments && commentsPermission > 1;
    const zoomContext = useContext(ZoomContext);
    const scale = zoomContext?.scale ?? 1;

    const containerRef = useRef<HTMLDivElement>(null);

    return (
      <QFlex
        flexShrink={1}
        flexGrow={1}
        justifyContent="space-between"
        bg="gray.50"
        border="1px"
        borderTop={isEditMode ? 'none' : '1px'}
        borderColor="gray.200"
        borderRadius={isEditMode ? '0 0 4px 4px' : '4px'}
        ref={ref}
        className={`${styles['document-content-container']}`}
        overflowY="hidden"
        overflowX="hidden"
      >
        <QFlex
          direction="column"
          flexShrink={1}
          flexGrow={1}
          ref={containerRef}
          className={`${styles['document-toc-container']}`}
          style={{
            minWidth: showTOC ? '48px' : '0px',
          }}
        >
          {isMultiRootEditor && (
            <QBox className={'document-outline-container-document'}></QBox>
          )}
          {!isMultiRootEditor && showTOC && (
            <TableOfContents
              editorName={editorName}
              sections={sections}
              sectionTitlesVisible={sectionTitlesVisible}
            />
          )}
          <QSpacer height="100%" />
          <BackToTop parentRef={containerRef} />
        </QFlex>

        <QFlex position="relative" height="fit-content">
          <QBox
            style={{
              zoom: `${scale}`,
            }}
          >
            <QBox
              my={4}
              ml={0}
              border="1px"
              borderColor="gray.200"
              boxShadow="md"
              id={contentId}
              data-cy={dataCy?.content}
              className={`${styles['document-section-content']} document ${
                !isEditMode && !isMultiRootEditor ? 'non-editable-mode' : ''
              }
            ${isLandscape ? 'landscape' : ''}`}
            >
              {children}
            </QBox>
          </QBox>
          {commentsEnabled && isMultiRootEditor && (
            <QBox
              px={2}
              py={4}
              id={commentControlId}
              data-cy={dataCy?.commentControl}
              className={`${styles['document-comment-control']}`}
            >
              <QStack
                direction="column"
                spacing={1}
                background="white"
                borderRadius="lg"
                p={2}
                id={'commentButtonsContainer'}
              >
                <QTooltip
                  label={t('tooltips.selectContentToAddComment')}
                  placement={'top'}
                >
                  <QIconButton
                    iconName={'MessageSquare'}
                    aria-label={t(
                      'accessibilityLabels.addCommentButtonAriaLabel',
                    )}
                    size="sm"
                    data-cy={dataCy?.commentButton}
                  />
                </QTooltip>
                <QTooltip
                  label={t('tooltips.openCommentHistory')}
                  placement={'top'}
                >
                  <QIconButton
                    iconName={'MessageCheck'}
                    aria-label={t(
                      'accessibilityLabels.openCommentHistoryButtonAriaLabel',
                    )}
                    size="sm"
                    data-cy={dataCy?.commentButton}
                  />
                </QTooltip>
              </QStack>
            </QBox>
          )}
          {commentsEnabled && !isMultiRootEditor && (
            <QBox
              px={2}
              py={4}
              id={commentControlId}
              data-cy={dataCy?.commentControl}
              className={`${styles['document-comment-control']}`}
            >
              <QTooltip
                label={t('tooltips.selectContentToAddComment')}
                placement={'top'}
              >
                <p>
                  <QIconButton
                    iconName={'MessageSquare'}
                    aria-label={t(
                      'accessibilityLabels.addCommentButtonAriaLabel',
                    )}
                    size="sm"
                    data-cy={dataCy?.commentButton}
                  />
                </p>
              </QTooltip>
            </QBox>
          )}
        </QFlex>

        {isMultiRootEditor && (
          <QBox
            id={sidebarId + '-revision-history'}
            className="revision-history__sidebar"
          ></QBox>
        )}

        <QBox
          className={`${styles['document-sidebar']}`}
          style={{
            minWidth: commentsEnabled ? '48px' : '0px',
          }}
        >
          {showComments && (
            <QBox
              onMouseDown={handleSidebarClick}
              flexShrink={1}
              id={sidebarId}
              data-cy={dataCy?.commentSidebar}
              className={`${
                editorStatus === EditorStatus.CONNECTING ? 'ck-hidden' : ''
              } transition_on_click`}
            />
          )}
        </QBox>
      </QFlex>
    );
  },
);
